/**
 * Horizontal swipe -> tab navigation (mobile): touchstart/touchend on
 * `document`, one hook call in App gives the whole app the gesture.
 * Ignored: touches that start inside `data-swipe-consume="true"` (the
 * charts' own scrub), mostly-vertical movements (page scrolls) and
 * anything under SWIPE_MIN_DX (taps + jitter).
 * CLAUDE.md > Front-end design tokens; docs/customer-app.md > Front-end > Swipe navigation
 */
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Must match the visible order in TabBar.tsx - swiping = "go to the
// tab next to the one I'm on" so the gesture has to use the same
// sequence the user sees in the tab strip.
const TABS = ["/", "/history", "/live", "/prijzen", "/prognose", "/account"] as const;
const SWIPE_MIN_DX = 60;
const HORIZONTAL_RATIO = 1.2;  // |dx| must be at least this × |dy|

export function useSwipeNav(): void {
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    // Skip when not on a tab page - login screen shouldn't navigate
    // on swipe (and there's nowhere meaningful to go).
    const tabIdx = TABS.indexOf(loc.pathname as (typeof TABS)[number]);
    if (tabIdx === -1) return;

    let startX = 0;
    let startY = 0;
    let active = false;

    function onStart(e: TouchEvent) {
      if (e.touches.length !== 1) {
        active = false;
        return;
      }
      const target = e.target as Element | null;
      // Element that wants to keep horizontal swipes for itself.
      if (target && target.closest('[data-swipe-consume="true"]')) {
        active = false;
        return;
      }
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      active = true;
    }

    function onEnd(e: TouchEvent) {
      if (!active) return;
      active = false;
      const t = e.changedTouches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (Math.abs(dx) < SWIPE_MIN_DX) return;
      if (Math.abs(dx) < Math.abs(dy) * HORIZONTAL_RATIO) return;

      // dx > 0 (swipe right) → previous tab; dx < 0 (swipe left) → next.
      const nextIdx = dx < 0 ? tabIdx + 1 : tabIdx - 1;
      if (nextIdx < 0 || nextIdx >= TABS.length) return;
      navigate(TABS[nextIdx]);
    }

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    document.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [loc.pathname, navigate]);
}
